import { FC, useMemo } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Typography from 'components/commercetools-ui/atoms/typography';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import BundleIcon from 'components/icons/bundle-icon';
import EbookIcon from 'components/icons/ebook-icon';
import PhysicalBookIcon from 'components/icons/physical-book-icon';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useClassNames from 'helpers/hooks/useClassNames';
import usePath from 'helpers/hooks/usePath';
import { ProductDetailsProps } from '..';
import ProductVariant from './product-variant';

type ProductInformationProps = Omit<ProductDetailsProps, 'onAddToCart'>;

const ProductInformation: FC<ProductInformationProps> = ({
  product,
  variant,
  onChangeVariant,
  inModalVersion,
  wishlist,
  removeLineItem,
  addToWishlist,
}) => {
  const router = useRouter();

  const { path } = usePath();

  const attributesToDisplay = ['color', 'finish', 'size'];

  const updateVariantSKU = (sku: string) => {
    router.replace(`/${path.split('/').filter(Boolean).slice(0, -1).join('/')}/${sku}`);
  };

  const productToWishlistLineItem = useMemo<LineItem>(() => {
    return {
      lineItemId: product?.productId ?? '',
      productId: product?.productId,
      productKey: product?.productKey,
      productRef: product?.productRef,
      name: product?.name,
      count: 1,
      variant: variant,
      addedAt: new Date(),
      _url: product?._url,
    };
  }, [product, variant]);

  const titleClassName = useClassNames(['break-normal mb-12']);

  return (
    <div>
      <div className="relative flex pr-40">
        <div className={`${titleClassName} text-sm font-normal leading-tight`}>
          <div className="flex items-center gap-6">
            {product?.productTypeKey === 'physical-book' ? (
              <div className="flex items-center gap-6">
                <PhysicalBookIcon className="size-24" />
                <Typography>Printed Book</Typography>
              </div>
            ) : product?.productTypeKey === 'ebook' ? (
              <div className="flex items-center gap-6">
                <EbookIcon className="size-24" />
                <Typography>eBook</Typography>
              </div>
            ) : product?.productTypeKey === 'bundle' ? (
              <div className="flex items-center gap-6">
                <BundleIcon className="size-24" />
                <Typography>Book & eBook</Typography>
              </div>
            ) : (
              <div className="flex items-center gap-6">
                <Typography>
                  {product?.productTypeKey
                    ?.replace(/-/g, ' ')
                    .split(' ')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </Typography>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="relative flex pr-40">
        <Typography as="h3" className={`${titleClassName} text-2xl font-bold leading-9 sm:text-3xl md:text-3xl`}>
          {product?.name}
        </Typography>

        <WishlistButton
          className="absolute right-40 cursor-pointer"
          lineItem={productToWishlistLineItem}
          data={wishlist}
          removeFromWishlist={removeLineItem}
          addToWishlist={addToWishlist}
        />
      </div>
      <div className="relative mt-2 flex flex-wrap pr-40 text-base font-normal leading-none text-gray-500">
        {variant?.attributes?.['author'] && (
          <>
            <Typography className="mr-4">Author:</Typography>
            <Typography className="mr-8 border-r border-gray-500 pr-8 text-blue-600">
              {variant?.attributes?.['author']}
            </Typography>
          </>
        )}
        {(variant?.attributes?.['publicationYear'] ||
          variant?.attributes?.['publicationMonth'] ||
          variant?.attributes?.['publicationDay']) && (
          <>
            <Typography className="mr-4">Publication date: </Typography>
            <Typography className="text-primary-black">
              {[
                `${variant?.attributes?.['publicationYear']}`,
                variant?.attributes?.['publicationMonth'] && variant?.attributes?.['publicationMonth'],
                variant?.attributes?.['publicationDay'] && variant?.attributes?.['publicationDay'],
              ]
                .filter(Boolean)
                .join('-')}
            </Typography>
          </>
        )}
      </div>
      {attributesToDisplay.map((attribute) => {
        if (variant?.attributes?.[attribute]) {
          return (
            <ProductVariant
              key={attribute}
              className="mt-20 border-b border-b-neutral-400 pb-20 md:mt-24"
              variants={product?.variants}
              currentVariant={variant}
              attribute={attribute}
              inModalVersion={inModalVersion}
              onClick={inModalVersion ? onChangeVariant : updateVariantSKU}
            />
          );
        }
      })}
    </div>
  );
};

export default ProductInformation;
